import { CheckCircle, CheckSquare, Square } from "@tamagui/lucide-icons";
import { action, computed, makeObservable, override, toJS } from "mobx";
import { ModuleType } from "../../../templates/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import BaseModuleModel, {
  type MenuOption,
  type ModuleModelData,
  type Snapshot,
} from "../core/BaseModuleModel";
import { getDefaultSettingsForModule } from "../utils";
import RsvpUserModel, {
  RsvpStatus,
  type RsvpVoteModelData,
} from "./data/RsvpUserModel";
import {
  RsvpModuleBlockUI,
  RsvpModuleStaticUI,
  RsvpModuleUI,
} from "./RsvpModuleUI";

export type RsvpModuleSnapshot = {
  numYes: number;
  numMaybe: number;
  numCold?: number;
};

export type RsvpModuleSettings = {
  showDescription: boolean;
  spotsLimit: number;
};

export interface RsvpModuleModelData
  extends ModuleModelData<RsvpModuleSnapshot> {
  settings: RsvpModuleSettings;
}

export type UserRsvpWithStatus = {
  userId: string;
  rsvp: RsvpStatus;
  comment?: string | null;
};

export default class RsvpModuleModel extends BaseModuleModel<
  RsvpModuleSnapshot,
  RsvpModuleModelData
> {
  constructor(stores: Stores, api: Api, data: RsvpModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      settings: getDefaultSettingsForModule(ModuleType.Rsvp, data.settings),
    });

    makeObservable(this);
  }

  // Used to show setting
  hasHelperGuide = true;

  @action
  async setDone() {
    this.doneFlowEnabled = false;
    this.updateSnapshotWithoutSave();
    await this.toggleDoneState();
  }

  @override onDone() {
    void this.setDone();
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = {
      numYes: this.comingUsers.length,
      numMaybe: this.maybeUsers.length,
    };
  }

  @action async addRsvp(rsvp: RsvpStatus, overrideUserUid?: string) {
    const userId = overrideUserUid ?? this.userUid;

    const existingUser = this.userItems.find((u) => u.data.userId === userId);

    if (existingUser) {
      await existingUser.setRsvp(rsvp);
    } else {
      await this.addNewData<RsvpVoteModelData>(ModuleDataType.RsvpUser, {
        userId,
        comment: null,
        lastChange: Date.now(),
        rsvp,
      });
    }

    await this.updateSnapshotWithoutSave();
    await this.save();
  }

  @action async updateSettings(
    settings: Partial<RsvpModuleSettings>,
    skipSave?: boolean,
  ) {
    this.data.settings = { ...this.data.settings, ...settings };

    if (!skipSave) {
      await this.api.modulesApi.set(this);
      this.sendTelemetryEvent("settings_updated");
    }
  }

  @computed get userItems(): RsvpUserModel[] {
    return (
      this.dataItems
        .filter((item): item is RsvpUserModel => {
          return RsvpUserModel.is(item);
        })
        .sort((a, b) => (a.data.lastChange > b.data.lastChange ? 1 : -1)) ?? []
    );
  }

  @computed get comingUsers(): UserRsvpWithStatus[] {
    return (
      this.userItems
        .filter((item) => {
          return item.data.rsvp === RsvpStatus.Coming;
        })
        .map((u) => toJS(u.data)) ?? []
    );
  }

  @computed get maybeUsers() {
    return (
      this.userItems
        .filter((item) => {
          return item.data.rsvp === RsvpStatus.Maybe;
        })
        .map((u) => toJS(u.data)) ?? []
    );
  }

  @computed get notComingUsers() {
    return (
      this.userItems
        .filter((item) => {
          return item.data.rsvp === RsvpStatus.No;
        })
        .map((u) => toJS(u.data)) ?? []
    );
  }

  @computed get notAnsweredUsers(): UserRsvpWithStatus[] {
    return (
      this.plan.users
        .filter((u) => {
          return !this.userItems.find((item) => u.uid === item.data.userId);
        })
        .map((u) => ({ userId: u.uid, rsvp: RsvpStatus.NoAnswerYet })) ?? []
    );
  }

  @computed get hotUsers(): UserRsvpWithStatus[] {
    return [...this.comingUsers, ...this.maybeUsers];
  }

  @computed get coldUsers(): UserRsvpWithStatus[] {
    return [...this.notComingUsers, ...this.notAnsweredUsers];
  }

  @computed get waitingForUsers(): string[] {
    return this.plan.users
      .filter((u) => {
        return !this.userItems.find((item) => u.uid === item.data.userId);
      })
      .map((u) => u.data.displayName);
  }

  @computed get hasAddedRsvp(): boolean {
    return !!this.userItems.find(
      (u) => u.data.userId === this.plan.userInPlan?.uid,
    );
  }

  @computed get currentRsvp(): RsvpUserModel | undefined {
    return this.userItems.find(
      (u) => u.data.userId === this.plan.userInPlan?.uid,
    );
  }

  render() {
    return <RsvpModuleUI module={this} />;
  }

  renderStatic() {
    return <RsvpModuleStaticUI module={this} />;
  }

  renderBlock() {
    return <RsvpModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return <RsvpModuleBlockUI module={this} />;
  }

  renderIcon(size?: number) {
    return <CheckCircle size={size} />;
  }

  renderMenuOptions(): MenuOption[] {
    return [
      {
        icon: this.data.settings.showDescription ? CheckSquare : Square,
        label: "Show description",
        onPress: () => {
          this.updateSettings({
            showDescription: !this.data.settings.showDescription,
          });
        },
      },
    ];
  }

  static is(
    module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>,
  ): module is RsvpModuleModel {
    return module instanceof RsvpModuleModel;
  }
}
